// Универсальная функция для запросов к backend
import { useUserStore } from '../store/userStore';

/**
 * Resolve the backend base URL. Priority:
 *  1. REACT_APP_API_URL (build-time env, used in dev / explicit overrides).
 *  2. Derive from the page hostname. Tenant frontends live on
 *     `<slug>.<domain>` with the backend on `api-<slug>.<domain>`. A frontend
 *     on its own apex domain (`skillscape.ru`) has its backend on `api.<host>`:
 *     `api-skillscape.ru` would be a different second-level domain that we do
 *     not own, and every request would carry the user's token there.
 *  3. Empty string (same-origin fallback for local hosts).
 */
export function resolveApiUrl(
  envUrl: string | undefined,
  location: Pick<Location, 'protocol' | 'hostname'> | undefined,
): string {
  if (envUrl) return envUrl;
  if (!location) return '';
  const { protocol } = location;
  // `skillscape.ru.` (FQDN with the root dot) is the same host; left as is it
  // counts as three labels and derives the foreign api-skillscape.ru. again.
  const hostname = location.hostname.replace(/\.$/, '');
  if (hostname === 'localhost' || hostname === '127.0.0.1') return '';
  const isApex = hostname.split('.').length === 2;
  return isApex ? `${protocol}//api.${hostname}` : `${protocol}//api-${hostname}`;
}

export const API_URL = resolveApiUrl(
  process.env.REACT_APP_API_URL,
  typeof window === 'undefined' ? undefined : window.location,
);

export class ApiError extends Error {
  status: number;
  response: { data: unknown; status: number };

  constructor(message: string, data: unknown, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.response = { data, status };
  }
}

export interface ApiOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
}

/**
 * Возвращает заголовки с токеном авторизации
 */
export function getAuthHeaders(extra?: Record<string, string>): Record<string, string> {
  const token = typeof window !== 'undefined' ? useUserStore.getState().token : null;
  const headers: Record<string, string> = { ...extra };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

/**
 * Общая логика выполнения запроса и обработки ответа
 */
async function executeRequest(url: string, fetchOptions: RequestInit) {
  let res: Response;
  try {
    res = await fetch(url, fetchOptions);
  } catch {
    // The request never reached the server or got no response (server
    // unreachable, CORS, dropped connection). fetch throws a raw TypeError
    // ("Failed to fetch") — wrap it as an ApiError with status 0 so callers can
    // show a friendly, localized message instead of the raw browser string.
    throw new ApiError('Network request failed', { networkError: true }, 0);
  }
  let data;
  try {
    data = await res.json();
  } catch {
    data = undefined;
  }
  if (!res.ok) {
    throw new ApiError(data?.error || data?.message || 'Ошибка запроса', data, res.status);
  }
  return data;
}

export async function api(path: string, options: ApiOptions = {}) {
  const headers = getAuthHeaders({ 'Content-Type': 'application/json', ...options.headers });

  const fetchOptions: RequestInit = {
    method: options.method || 'GET',
    headers,
  };
  if (options.body !== undefined) {
    fetchOptions.body = JSON.stringify(options.body);
  }

  return executeRequest(`${API_URL}${path}`, fetchOptions);
}

/**
 * Извлекает осмысленное сообщение об ошибке из ответа API
 */
/**
 * Pull `{ message, error, errors[] }` out of any response payload — works for
 * both our fetch-based ApiError and axios errors (used by orval-generated
 * clients). Order: `errors[]` (joined) → `error` → `message`.
 */
const extractFromBody = (data: unknown): string | undefined => {
  if (!data || typeof data !== 'object') return undefined;
  const record = data as Record<string, unknown>;
  if (Array.isArray(record.errors)) {
    return record.errors
      .map((e: unknown) =>
        e && typeof e === 'object' && 'message' in e
          ? String((e as { message: string }).message)
          : String(e),
      )
      .join(', ');
  }
  if (typeof record.error === 'string') return record.error;
  if (typeof record.message === 'string') return record.message;
  return undefined;
};

export const getErrorMessage = (
  error: unknown,
  defaultMessage: string = 'Произошла ошибка',
): string => {
  if (error instanceof ApiError) {
    return extractFromBody(error.response.data) || error.message;
  }

  // Axios error shape (used by orval-generated clients). The message we want
  // lives on `.response.data.message`, NOT on `.message` (which is the generic
  // "Request failed with status code N").
  if (error && typeof error === 'object' && 'response' in error) {
    const resp = (error as { response?: { data?: unknown } }).response;
    const fromBody = extractFromBody(resp?.data);
    if (fromBody) return fromBody;
  }

  if (error instanceof Error) return error.message;

  return defaultMessage;
};

/**
 * Функция для отправки FormData (файлы и данные формы)
 */
export async function apiFormData(path: string, options: ApiOptions = {}) {
  const headers = getAuthHeaders(options.headers);

  const fetchOptions: RequestInit = {
    method: options.method || 'POST',
    headers,
  };
  if (options.body !== undefined) {
    fetchOptions.body = options.body as BodyInit;
  }

  return executeRequest(`${API_URL}${path}`, fetchOptions);
}
